import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useGoogleFonts } from '../../utils/fontLoader';
import { Navigation } from '../../components/landing/NavigationSection';
import { Footer } from '../../components/landing/FooterSection';
import { HeroSection } from '../../components/landing/HeroSection';
import { TemplatesSection } from '../../components/landing/TemplatesSection';
import { ThemeDemosSection } from '../../components/landing/ThemeDemosSection';
import { HeadlineFeaturesSection } from '../../components/landing/HeadlineFeaturesSection';
import { FeaturesGridSection } from '../../components/landing/FeaturesGridSection';
import { HowItWorksSection } from '../../components/landing/HowItWorksSection';
import { WeddingPlannerSection } from '../../components/landing/WeddingPlannerSection';
import { SeatingPlannerSection } from '../../components/landing/SeatingPlannerSection';
import { EmotiveSection } from '../../components/landing/EmotiveSection';
import { TestimonialsSection } from '../../components/landing/TestimonialsSection';
import { PricingSection } from '../../components/landing/PricingSection';
import { FAQSection } from '../../components/landing/FAQSection';
import { OurStorySection } from '../../components/landing/OurStorySection';
import { getHomepageStrings, SUPPORTED_HOMEPAGE_LOCALES } from '../../i18n/homepage';

// Re-export Navigation and Footer so existing imports still work
export { Navigation } from '../../components/landing/NavigationSection';
export { Footer } from '../../components/landing/FooterSection';

// DashboardMockup — kept here for backwards compatibility (used in other pages)
export { DashboardMockup } from './DashboardMockup';

export function LandingPage({ locale = 'en' }: { locale?: string }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [showFloatingCta, setShowFloatingCta] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  useGoogleFonts(['Cormorant Garamond', 'Quicksand', 'Jost', 'Playfair Display', 'Montserrat']);

  const strings = getHomepageStrings(locale);

  // One-time auto-detect: on the English homepage, redirect to the visitor's
  // browser language if it matches a supported locale. Only runs once per session.
  useEffect(() => {
    if (locale !== 'en') return;
    if (typeof window === 'undefined') return;
    if (sessionStorage.getItem('locale-redirect')) return;
    const browserLocale = navigator.language.slice(0, 2).toLowerCase();
    if ((SUPPORTED_HOMEPAGE_LOCALES as readonly string[]).includes(browserLocale)) {
      sessionStorage.setItem('locale-redirect', browserLocale);
      navigate('/' + browserLocale, { replace: true });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const check = () => {
      const hero = document.getElementById('hero');
      if (!hero) return;
      setShowFloatingCta(hero.getBoundingClientRect().bottom < 0);
    };
    window.addEventListener('scroll', check, { passive: true });
    return () => window.removeEventListener('scroll', check);
  }, []);

  useEffect(() => {
    if (location.hash) {
      const id = location.hash.replace('#', '');
      setTimeout(() => {
        const element = document.getElementById(id);
        if (element) {
          const offset = 80;
          const elementPosition = element.getBoundingClientRect().top + window.scrollY;
          window.scrollTo({ top: elementPosition - offset, behavior: 'smooth' });
        }
      }, 100);
    }
  }, [location.hash]);

  return (
    <div className="min-h-screen bg-background">
      <Navigation strings={strings} onMenuOpenChange={setMobileMenuOpen} />
      <main>
        <HeroSection strings={strings} />
        <ThemeDemosSection
          badge={strings.theme_demos_badge}
          heading={<>{strings.theme_demos_heading_pre}<span className="text-primary">{strings.theme_demos_heading_highlight}</span></>}
          subheading={strings.theme_demos_subheading}
          ctaLabel={strings.theme_demos_cta}
        />
        <TemplatesSection strings={strings} />
        <HeadlineFeaturesSection strings={strings} />
        <FeaturesGridSection strings={strings} />
        <HowItWorksSection strings={strings} />
        <WeddingPlannerSection strings={strings} />
        <SeatingPlannerSection strings={strings} />
        <EmotiveSection statements={strings.emotive_statements} />
        <TestimonialsSection strings={strings} />
        <PricingSection strings={strings} locale={locale} />
        <FAQSection
          faqItems={strings.faq_items}
          heading={strings.faq_heading}
          headingHighlight={strings.faq_heading_highlight}
          subtitle={strings.faq_subtitle}
          contactLinkText={strings.faq_contact_link}
          contactSuffix={strings.faq_contact_suffix}
        />
        <OurStorySection strings={strings} />
      </main>
      <Footer strings={strings} />
      {/* Floating mobile CTA */}
      <div className="md:hidden">
        <div
          className="fixed bottom-0 left-0 right-0 z-50 p-4 pb-6 bg-gradient-to-t from-background via-background/90 to-transparent pointer-events-none transition-transform duration-300 ease-out"
          style={{ transform: (showFloatingCta && !mobileMenuOpen) ? 'translateY(0)' : 'translateY(110%)' }}
        >
          <Link
            to="/register"
            className="pointer-events-auto w-full flex items-center justify-center py-3.5 bg-primary text-text-inverse rounded-2xl font-semibold text-sm shadow-lg active:scale-95 transition-transform"
          >
            {strings.hero_cta_primary} →
          </Link>
        </div>
      </div>
    </div>
  );
}
